// =============================================================================
// GESTIONNAIRE DE VARIABLES - Templates Documents
// =============================================================================
// Interface pour gérer les variables utilisables dans les templates : {{nom}}.
// Les LISTES (statuts, types, civilités, etc.) sont gérées dans
// Gouvernance > Listes (collection metier_config_listes, hook useConfigList).
// CC-15 — déduplication 2026-05-05.
// =============================================================================

import { useState, useEffect } from "react";
import {
  Variable,
  Plus,
  Edit,
  Trash2,
  Search,
  Save,
  X,
  Copy,
  CheckCircle,
  Info,
  FileText,
  User,
  Home,
  Euro,
  Calendar,
  Hash,
  ToggleLeft,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Settings,
  Building2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// CONSTANTES - SOURCES VARIABLES
// =============================================================================

const SOURCES = [
  { value: "client", label: "Client", icon: User, color: "bg-[var(--oh-accent-solid)]" },
  { value: "dossier", label: "Dossier", icon: FileText, color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
  { value: "devis", label: "Devis", icon: Euro, color: "bg-yellow-500" },
  { value: "logement", label: "Logement", icon: Home, color: "bg-[var(--oh-accent-solid)]" },
  { value: "installateur", label: "Installateur", icon: User, color: "bg-orange-500" },
  { value: "aides", label: "Aides", icon: Euro, color: "bg-emerald-500" },
  { value: "chantier", label: "Chantier", icon: Home, color: "bg-red-500" },
  { value: "calcule", label: "Calculée", icon: Hash, color: "bg-zinc-500/20 border border-[var(--oh-border)]" },
  { value: "reglementaire", label: "Réglementaire CEE", icon: FileText, color: "bg-cyan-500/15 border border-cyan-500/30" },
  { value: "pac", label: "PAC (BAR-TH-171)", icon: Settings, color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" },
  { value: "cet", label: "CET (BAR-TH-148)", icon: Settings, color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]" },
  { value: "operation", label: "Opération/Deal", icon: FileText, color: "bg-pink-500" },
  { value: "signature", label: "Signature/Légal", icon: FileText, color: "bg-amber-500" },
  { value: "entreprise", label: "Entreprise", icon: Building2, color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]" },
];

const TYPES_DONNEE = [
  { value: "texte", label: "Texte", icon: FileText },
  { value: "nombre", label: "Nombre", icon: Hash },
  { value: "montant", label: "Montant (€)", icon: Euro },
  { value: "date", label: "Date", icon: Calendar },
  { value: "booleen", label: "Oui/Non", icon: ToggleLeft },
];

// Variables système par défaut (non modifiables)
const VARIABLES_SYSTEME = [
  // =============================================================================
  // CLIENT
  // =============================================================================
  { nom: "client_nom", source: "client", type_donnee: "texte", description: "Nom du client", systeme: true },
  { nom: "client_prenom", source: "client", type_donnee: "texte", description: "Prénom du client", systeme: true },
  { nom: "client_adresse", source: "client", type_donnee: "texte", description: "Adresse complète", systeme: true },
  { nom: "client_code_postal", source: "client", type_donnee: "texte", description: "Code postal", systeme: true },
  { nom: "client_ville", source: "client", type_donnee: "texte", description: "Ville", systeme: true },
  { nom: "client_telephone", source: "client", type_donnee: "texte", description: "Téléphone", systeme: true },
  { nom: "client_email", source: "client", type_donnee: "texte", description: "Email", systeme: true },
  
  // =============================================================================
  // LOGEMENT
  // =============================================================================
  { nom: "logement_adresse", source: "logement", type_donnee: "texte", description: "Adresse du logement", systeme: true },
  { nom: "logement_type", source: "logement", type_donnee: "texte", description: "Type (maison/appartement)", systeme: true },
  { nom: "logement_surface", source: "logement", type_donnee: "nombre", description: "Surface en m²", systeme: true },
  { nom: "logement_annee_construction", source: "logement", type_donnee: "nombre", description: "Année de construction", systeme: true },
  { nom: "logement_zone_climatique", source: "logement", type_donnee: "texte", description: "Zone climatique (H1/H2/H3)", systeme: true },
  { nom: "residence_type", source: "logement", type_donnee: "texte", description: "Type de résidence (principale/secondaire)", systeme: true },
  { nom: "statut_occupation", source: "logement", type_donnee: "texte", description: "Statut occupation (propriétaire/locataire)", systeme: true },
  { nom: "ancienne_energie", source: "logement", type_donnee: "texte", description: "Ancienne énergie (gaz/fioul/charbon/électrique)", systeme: true },
  
  // =============================================================================
  // DEVIS
  // =============================================================================
  { nom: "devis_numero", source: "devis", type_donnee: "texte", description: "Numéro du devis", systeme: true },
  { nom: "devis_date", source: "devis", type_donnee: "date", description: "Date du devis", systeme: true },
  { nom: "devis_total_ht", source: "devis", type_donnee: "montant", description: "Total HT", systeme: true },
  { nom: "devis_total_ttc", source: "devis", type_donnee: "montant", description: "Total TTC", systeme: true },
  
  // =============================================================================
  // AIDES
  // =============================================================================
  { nom: "montant_cee", source: "aides", type_donnee: "montant", description: "Montant CEE estimé", systeme: true },
  { nom: "montant_mpr", source: "aides", type_donnee: "montant", description: "Montant MaPrimeRénov'", systeme: true },
  { nom: "reste_a_charge", source: "aides", type_donnee: "montant", description: "Reste à charge client", systeme: true },
  
  // =============================================================================
  // INSTALLATEUR
  // =============================================================================
  { nom: "installateur_nom", source: "installateur", type_donnee: "texte", description: "Nom de l'entreprise", systeme: true },
  { nom: "installateur_siret", source: "installateur", type_donnee: "texte", description: "SIRET", systeme: true },
  { nom: "installateur_rge", source: "installateur", type_donnee: "texte", description: "Numéro RGE", systeme: true },
  
  // =============================================================================
  // DOSSIER
  // =============================================================================
  { nom: "dossier_reference", source: "dossier", type_donnee: "texte", description: "Référence dossier", systeme: true },
  { nom: "dossier_date_creation", source: "dossier", type_donnee: "date", description: "Date de création", systeme: true },
  
  // =============================================================================
  // DATES CALCULÉES
  // =============================================================================
  { nom: "date_jour", source: "calcule", type_donnee: "date", description: "Date du jour", systeme: true },
  
  // =============================================================================
  // DATES RÉGLEMENTAIRES CEE
  // =============================================================================
  { nom: "engagement_date", source: "reglementaire", type_donnee: "date", description: "Date d'engagement du bénéficiaire", systeme: true },
  { nom: "facture_date", source: "reglementaire", type_donnee: "date", description: "Date de la facture", systeme: true },
  { nom: "date_mise_en_service", source: "reglementaire", type_donnee: "date", description: "Date de mise en service", systeme: true },
  
  // =============================================================================
  // PAC - BAR-TH-171
  // =============================================================================
  { nom: "pac_type", source: "pac", type_donnee: "texte", description: "Type de PAC (air/eau, air/air, géothermique)", systeme: true },
  { nom: "pac_marque", source: "pac", type_donnee: "texte", description: "Marque de la PAC", systeme: true },
  { nom: "pac_reference", source: "pac", type_donnee: "texte", description: "Référence modèle PAC", systeme: true },
  { nom: "pac_etas", source: "pac", type_donnee: "nombre", description: "ETAS (Efficacité Énergétique Saisonnière)", systeme: true },
  { nom: "pac_usage", source: "pac", type_donnee: "texte", description: "Usage (chauffage, chauffage+ECS)", systeme: true },
  { nom: "pac_puissance", source: "pac", type_donnee: "nombre", description: "Puissance nominale (kW)", systeme: true },
  { nom: "pac_classe_regulation", source: "pac", type_donnee: "texte", description: "Classe de régulation", systeme: true },
  
  // =============================================================================
  // CET - BAR-TH-148
  // =============================================================================
  { nom: "cet_type", source: "cet", type_donnee: "texte", description: "Type de CET (thermodynamique)", systeme: true },
  { nom: "cet_marque", source: "cet", type_donnee: "texte", description: "Marque du CET", systeme: true },
  { nom: "cet_reference", source: "cet", type_donnee: "texte", description: "Référence modèle CET", systeme: true },
  { nom: "cet_capacite", source: "cet", type_donnee: "nombre", description: "Capacité (litres)", systeme: true },
  { nom: "cet_cop", source: "cet", type_donnee: "nombre", description: "COP (Coefficient de Performance)", systeme: true },
  { nom: "cet_volume_ballon", source: "cet", type_donnee: "nombre", description: "Volume du ballon (litres)", systeme: true },
  
  // =============================================================================
  // OPÉRATION / DEAL PREMIUM
  // =============================================================================
  { nom: "deal_type", source: "operation", type_donnee: "texte", description: "Type de deal (PREMIUM, STANDARD)", systeme: true },
  { nom: "operation_code", source: "operation", type_donnee: "texte", description: "Code opération CEE (BAR-TH-171, BAR-TH-148...)", systeme: true },
  { nom: "operation_label", source: "operation", type_donnee: "texte", description: "Libellé de l'opération", systeme: true },
  { nom: "phase_operation", source: "operation", type_donnee: "texte", description: "Phase (avant_travaux, apres_travaux)", systeme: true },
  
  // =============================================================================
  // SIGNATURE / LÉGAL
  // =============================================================================
  { nom: "ville_signature", source: "signature", type_donnee: "texte", description: "Ville de signature", systeme: true },
  { nom: "date_signature_client", source: "signature", type_donnee: "date", description: "Date signature client", systeme: true },
  { nom: "date_signature_installateur", source: "signature", type_donnee: "date", description: "Date signature installateur", systeme: true },
  
  // =============================================================================
  // ENTREPRISE (MENTIONS LÉGALES)
  // =============================================================================
  { nom: "entreprise_nom", source: "entreprise", type_donnee: "texte", description: "Raison sociale de l'entreprise", systeme: true },
  { nom: "assurance_decennale_numero", source: "entreprise", type_donnee: "texte", description: "N° assurance décennale", systeme: true },
  { nom: "assurance_rcpro_numero", source: "entreprise", type_donnee: "texte", description: "N° assurance RC Pro", systeme: true },
  
  // =============================================================================
  // MÉNAGE (CATÉGORIE CEE)
  // =============================================================================
  { nom: "nombre_personnes_foyer", source: "client", type_donnee: "nombre", description: "Nombre de personnes dans le foyer", systeme: true },
  { nom: "revenu_fiscal_reference", source: "client", type_donnee: "montant", description: "Revenu fiscal de référence (RFR)", systeme: true },
  { nom: "zone_revenu", source: "client", type_donnee: "texte", description: "Zone de revenu (IDF/Province)", systeme: true },
  { nom: "categorie_menage", source: "client", type_donnee: "texte", description: "Catégorie ménage (très modeste, modeste, intermédiaire, supérieur)", systeme: true },
  
  // =============================================================================
  // PARTENAIRE / DÉLÉGATAIRE CEE (CADRE CONTRIBUTION)
  // =============================================================================
  { nom: "partenaire_cee_nom", source: "aides", type_donnee: "texte", description: "Nom du partenaire/délégataire CEE", systeme: true },
  { nom: "partenaire_cee_url", source: "aides", type_donnee: "texte", description: "URL du site du partenaire CEE", systeme: true },
  { nom: "partenaire_cee_tel", source: "aides", type_donnee: "texte", description: "Téléphone du partenaire CEE", systeme: true },
  
  // =============================================================================
  // TRAVAUX / VOLUMES CEE
  // =============================================================================
  { nom: "travaux_nature", source: "chantier", type_donnee: "texte", description: "Nature des travaux réalisés", systeme: true },
  { nom: "cee_prime_montant_eur", source: "aides", type_donnee: "montant", description: "Montant de la prime CEE en euros", systeme: true },
  { nom: "cee_volume_mwh_cumac", source: "aides", type_donnee: "nombre", description: "Volume CEE en MWh cumac", systeme: true },
  { nom: "cee_volume_kwh_cumac", source: "aides", type_donnee: "nombre", description: "Volume CEE en kWh cumac", systeme: true },
  { nom: "cee_precarite_mwh_cumac", source: "aides", type_donnee: "nombre", description: "Volume CEE précarité en MWh cumac", systeme: true },
  { nom: "cee_precarite_kwh_cumac", source: "aides", type_donnee: "nombre", description: "Volume CEE précarité en kWh cumac", systeme: true },
  
  // =============================================================================
  // FAIRE / FRANCE RÉNOV' / MÉDIATEUR
  // =============================================================================
  { nom: "faire_url", source: "calcule", type_donnee: "texte", description: "URL du site FAIRE / France Rénov'", systeme: true },
  { nom: "faire_tel", source: "calcule", type_donnee: "texte", description: "Téléphone FAIRE / France Rénov'", systeme: true },
  { nom: "mediateur_nom", source: "entreprise", type_donnee: "texte", description: "Nom du médiateur de la consommation", systeme: true },
  { nom: "mediateur_adresse", source: "entreprise", type_donnee: "texte", description: "Adresse du médiateur", systeme: true },
  { nom: "mediateur_url", source: "entreprise", type_donnee: "texte", description: "Site web du médiateur", systeme: true },
  { nom: "mediateur_tel", source: "entreprise", type_donnee: "texte", description: "Téléphone du médiateur", systeme: true },
  
  // =============================================================================
  // ALIAS MENTIONS OBLIGATOIRES
  // =============================================================================
  { nom: "aide_mpr", source: "aides", type_donnee: "montant", description: "Montant MaPrimeRénov' (alias)", systeme: true },
  { nom: "aide_cee", source: "aides", type_donnee: "montant", description: "Montant prime CEE (alias)", systeme: true },
  { nom: "entreprise_rge", source: "installateur", type_donnee: "texte", description: "Numéro RGE de l'entreprise (alias)", systeme: true },
  { nom: "reste_charge", source: "aides", type_donnee: "montant", description: "Reste à charge (alias)", systeme: true },
];

// =============================================================================
// COMPOSANT GESTION DES VARIABLES
// =============================================================================

function VariablesTab({ tenantId }) {
  const [variablesPersonnalisees, setVariablesPersonnalisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [copiedVar, setCopiedVar] = useState(null);
  
  const [newVariable, setNewVariable] = useState({
    nom: "",
    source: "client",
    champ_source: "",
    type_donnee: "texte",
    description: "",
    valeur_defaut: "",
    format: ""
  });

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/documents-engine/admin/variables`, {
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setVariablesPersonnalisees(data.variables_personnalisees || []);
      }
    } catch (err) {
      console.error("Erreur chargement variables:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyVariable = (nom) => {
    const varText = `{{${nom}}}`;
    navigator.clipboard.writeText(varText);
    setCopiedVar(nom);
    toast.success(`Variable copiée: ${varText}`);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const handleSaveVariable = async () => {
    if (!newVariable.nom || !newVariable.description) {
      toast.error("Le nom et la description sont obligatoires");
      return;
    }
    
    if (!/^[a-z][a-z0-9_]*$/.test(newVariable.nom)) {
      toast.error("Le nom doit être en snake_case (ex: ma_variable)");
      return;
    }
    
    const allVars = [...VARIABLES_SYSTEME, ...variablesPersonnalisees];
    if (allVars.some(v => v.nom === newVariable.nom) && !editingVariable) {
      toast.error("Une variable avec ce nom existe déjà");
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const method = editingVariable ? "PUT" : "POST";
      const url = editingVariable 
        ? `${API}/api/metier/documents-engine/admin/variables/${editingVariable.nom}`
        : `${API}/api/metier/documents-engine/admin/variables`;
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: JSON.stringify(newVariable)
      });
      
      if (res.ok) {
        toast.success(editingVariable ? "Variable modifiée" : "Variable créée");
        setShowAddDialog(false);
        setEditingVariable(null);
        setNewVariable({ nom: "", source: "client", champ_source: "", type_donnee: "texte", description: "", valeur_defaut: "", format: "" });
        loadVariables();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la sauvegarde");
      }
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDeleteVariable = async (nom) => {
    if (!confirm(`Supprimer la variable "${nom}" ?`)) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/documents-engine/admin/variables/${nom}`, {
        method: "DELETE",
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        }
      });
      
      if (res.ok) {
        toast.success("Variable supprimée");
        loadVariables();
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const allVariables = [...VARIABLES_SYSTEME, ...variablesPersonnalisees];
  const filteredVariables = allVariables.filter(v => {
    const matchSearch = !searchTerm || 
      v.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.description && v.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchSource = filterSource === "all" || v.source === filterSource;
    return matchSearch && matchSource;
  });

  const groupedVariables = filteredVariables.reduce((acc, v) => {
    if (!acc[v.source]) acc[v.source] = [];
    acc[v.source].push(v);
    return acc;
  }, {});

  const getSourceInfo = (source) => SOURCES.find(s => s.value === source) || SOURCES[0];
  const getTypeInfo = (type) => TYPES_DONNEE.find(t => t.value === type) || TYPES_DONNEE[0];
  const toggleSource = (source) => setExpandedSources(prev => ({ ...prev, [source]: !prev[source] }));

  return (
    <div className="space-y-4">
      {/* Info */}
      <Card className="bg-cyan-500/10 border-cyan-500/30">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-cyan-300">
                Utilisez les variables dans vos templates avec la syntaxe <code className="bg-[var(--oh-bg-elevated)] px-1.5 py-0.5 rounded">{"{{nom_variable}}"}</code>
              </p>
              <p className="text-cyan-400/70 mt-1">
                Cliquez sur une variable pour la copier dans le presse-papier.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--oh-text-muted)]" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
            />
          </div>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-40 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {SOURCES.map(source => (
                <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-[var(--oh-text-muted)]">{filteredVariables.length} var.</Badge>
        </div>
        <Button
          onClick={() => {
            setEditingVariable(null);
            setNewVariable({ nom: "", source: "client", champ_source: "", type_donnee: "texte", description: "", valeur_defaut: "", format: "" });
            setShowAddDialog(true);
          }}
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle variable
        </Button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(groupedVariables).map(([source, vars]) => {
            const sourceInfo = getSourceInfo(source);
            const SourceIcon = sourceInfo.icon;
            const isExpanded = expandedSources[source] === true;  // Fermé par défaut
            
            return (
              <Collapsible key={source} open={isExpanded} onOpenChange={() => toggleSource(source)}>
                <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <CardHeader className="py-2 cursor-pointer hover:bg-[var(--oh-bg-elevated)]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${sourceInfo.color}`}>
                            <SourceIcon className="w-3 h-3 text-[var(--oh-text)]" />
                          </div>
                          <span className="text-[var(--oh-text)] text-sm font-medium">{sourceInfo.label}</span>
                          <Badge variant="outline" className="text-xs">{vars.length}</Badge>
                        </div>
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-[var(--oh-text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--oh-text-muted)]" />}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1">
                        {vars.map((v) => {
                          const typeInfo = getTypeInfo(v.type_donnee);
                          const isCopied = copiedVar === v.nom;
                          
                          return (
                            <div key={v.nom} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--oh-bg-elevated)]">
                              <button onClick={() => copyVariable(v.nom)} className="flex items-center gap-2 group">
                                <code className="bg-[var(--oh-bg-surface)] px-2 py-0.5 rounded text-[var(--oh-stat-cyan-text)] font-mono text-xs group-hover:bg-cyan-900/30">
                                  {`{{${v.nom}}}`}
                                </code>
                                {isCopied ? <CheckCircle className="w-3 h-3 text-[var(--oh-success-text)]" /> : <Copy className="w-3 h-3 text-[var(--oh-text-secondary)] opacity-0 group-hover:opacity-100" />}
                              </button>
                              <div className="flex items-center gap-3">
                                <span className="text-[var(--oh-text-secondary)] text-xs">{v.description}</span>
                                {v.systeme ? (
                                  <Badge variant="outline" className="text-[var(--oh-text-secondary)] text-[10px]">Système</Badge>
                                ) : (
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteVariable(v.nom)} className="h-6 w-6 p-0 text-[var(--oh-error-text)]">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-lg" aria-describedby="dialog-variable-description">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Variable className="w-5 h-5 text-cyan-400" />
              {editingVariable ? "Modifier" : "Nouvelle variable"}
            </DialogTitle>
            <p id="dialog-variable-description" className="text-sm text-[var(--oh-text-muted)]">
              {editingVariable ? "Modifiez les informations de la variable" : "Créez une nouvelle variable personnalisée pour vos templates"}
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Nom *</Label>
                <Input
                  value={newVariable.nom}
                  onChange={(e) => setNewVariable({ ...newVariable, nom: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="ma_variable"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
                  disabled={!!editingVariable}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Source</Label>
                <Select value={newVariable.source} onValueChange={(v) => setNewVariable({ ...newVariable, source: v })}>
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[var(--oh-text-muted)]">Description *</Label>
              <Input
                value={newVariable.description}
                onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
                placeholder="Description de la variable"
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Type</Label>
                <Select value={newVariable.type_donnee} onValueChange={(v) => setNewVariable({ ...newVariable, type_donnee: v })}>
                  <SelectTrigger className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_DONNEE.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[var(--oh-text-muted)]">Valeur défaut</Label>
                <Input
                  value={newVariable.valeur_defaut}
                  onChange={(e) => setNewVariable({ ...newVariable, valeur_defaut: e.target.value })}
                  placeholder="Valeur par défaut"
                  className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveVariable} className="bg-cyan-600 hover:bg-cyan-700">
              <Save className="w-4 h-4 mr-2" />
              {editingVariable ? "Modifier" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export default function VariablesManager() {
  const tenantId = localStorage.getItem("tenantId") || "climatpac";

  return (
    <div className="space-y-6" data-testid="variables-manager">
      {/* En-tête */}
      <div>
        <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
          <Variable className="w-5 h-5 text-cyan-400" />
          Variables Templates
        </h2>
        <p className="text-[var(--oh-text-muted)] text-sm mt-1">
          Gérez les variables utilisables dans vos templates de documents.
          {" "}
          <span className="text-[var(--oh-text-secondary)]">
            Les listes (statuts, types, civilités…) sont gérées dans
            {" "}
            <strong
              className="text-cyan-400"
              data-testid="hint-gouvernance-listes"
            >
              Gouvernance &gt; Listes &amp; Statuts
            </strong>
            .
          </span>
        </p>
      </div>

      <VariablesTab tenantId={tenantId} />
    </div>
  );
}
